import React, { useState } from 'react';
import StoppageEvent from '../eventTypes/StoppageEvent';
import HitEvent from '../eventTypes/HitEvent';
import GoalEvent from '../eventTypes/GoalEvent';
import PenaltyEvent from '../eventTypes/PenaltyEvent';
import ShotOnGoalEvent from '../eventTypes/ShotOnGoalEvent';
import FaceoffEvent from '../eventTypes/FaceoffEvent';
import BlockEvent from '../eventTypes/BlockEvent';
import TakeawayEvent from '../eventTypes/TakeawayEvent';
import GiveawayEvent from '../eventTypes/GiveawayEvent';
import MissedShotEvent from '../eventTypes/MissedShotEvent';
import PeriodStartEvent from '../eventTypes/PeriodStartEvent';

const GameEventList = ({ events, homeTeamId, awayTeamId, homeTeamAbbrev, awayTeamAbbrev, players }) => {
    const [selectedEventTypes, setSelectedEventTypes] = useState([]);
    const [periodFilter, setPeriodFilter] = useState('');
    const [playerFilter, setPlayerFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const eventsPerPage = 20;

    const eventTypes = [
        'stoppage',
        'hit',
        'goal',
        'penalty',
        'shot-on-goal',
        'faceoff',
        'blocked-shot',
        'takeaway',
        'giveaway',
        'missed-shot',
        'period-start'
    ];

    const getPlayerName = (playerId) => {
        const player = players.find(p => p.id === playerId);
        return player ? player.fullName : 'N/A';
    };

    const getTeamAbbreviation = (teamId) => {
        if (teamId === homeTeamId) return homeTeamAbbrev;
        if (teamId === awayTeamId) return awayTeamAbbrev;
        return '';
    };

    const isPlayerInvolved = (event, playerName) => {
        const involvedPlayerIds = [
            event.details?.blockingPlayerId,
            event.details?.shootingPlayerId,
            event.details?.penaltyOnId,
            event.details?.penaltyDrawnById,
            event.details?.goalScorerId,
            event.details?.assistPlayerId1,
            event.details?.assistPlayerId2,
            event.details?.hitteePlayerId,
            event.details?.hitterPlayerId,
            event.details?.faceoffWonByPlayerId,
            event.details?.faceoffLostByPlayerId,
            event.details?.turnoverPlayerId,
            event.details?.takeawayPlayerId
        ];
        return involvedPlayerIds.some(playerId => getPlayerName(playerId).toLowerCase().includes(playerName.toLowerCase()));
    };

    const toggleEventType = (eventType) => {
        setSelectedEventTypes(prev =>
            prev.includes(eventType) ? prev.filter(type => type !== eventType) : [...prev, eventType]
        );
    };

    const filteredEvents = events.filter(event => {
        if (selectedEventTypes.length > 0 && !selectedEventTypes.includes(event.typeDescKey)) {
            return false;
        }
        if (periodFilter && event.periodDescriptor.number.toString() !== periodFilter) {
            return false;
        }
        if (playerFilter && !isPlayerInvolved(event, playerFilter)) {
            return false;
        }
        return true;
    });

    const indexOfLastEvent = currentPage * eventsPerPage;
    const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
    const currentEvents = filteredEvents.slice(indexOfFirstEvent, indexOfLastEvent);

    const paginate = pageNumber => setCurrentPage(pageNumber);

    return (
        <div className="bg-white shadow rounded-lg p-6 mt-6">
            <h2 className="text-2xl font-bold mb-4">Game Events</h2>
            <div className="mb-4">
                <div className="mb-4">
                    {eventTypes.map(eventType => (
                        <label key={eventType} className="inline-flex items-center mr-4">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                checked={selectedEventTypes.includes(eventType)}
                                onChange={() => toggleEventType(eventType)}
                            />
                            <span className="ml-2">{eventType}</span>
                        </label>
                    ))}
                </div>
                <input
                    type="text"
                    placeholder="Filter events by period"
                    value={periodFilter}
                    onChange={(e) => setPeriodFilter(e.target.value)}
                    className="p-2 border rounded w-full mb-2"
                />
                <input
                    type="text"
                    placeholder="Filter events by player name"
                    value={playerFilter}
                    onChange={(e) => setPlayerFilter(e.target.value)}
                    className="p-2 border rounded w-full"
                />
            </div>
            <ul className="list-none pl-0">
                {currentEvents.map((event, index) => (
                    <li key={index} className="mb-4 p-4 border rounded-lg shadow-sm bg-gray-50 cursor-pointer" onClick={() => setSelectedEvent(event)}>
                        {event.typeDescKey === 'stoppage' && <StoppageEvent event={event} />}
                        {event.typeDescKey === 'hit' && <HitEvent event={event} getPlayerName={getPlayerName} getTeamAbbreviation={getTeamAbbreviation} homeTeamId={homeTeamId} awayTeamId={awayTeamId} />}
                        {event.typeDescKey === 'goal' && <GoalEvent event={event} getPlayerName={getPlayerName} getTeamAbbreviation={getTeamAbbreviation} />}
                        {event.typeDescKey === 'penalty' && <PenaltyEvent event={event} getPlayerName={getPlayerName} getTeamAbbreviation={getTeamAbbreviation} />}
                        {event.typeDescKey === 'shot-on-goal' && <ShotOnGoalEvent event={event} getPlayerName={getPlayerName} getTeamAbbreviation={getTeamAbbreviation} />}
                        {event.typeDescKey === 'faceoff' && <FaceoffEvent event={event} getPlayerName={getPlayerName} getTeamAbbreviation={getTeamAbbreviation} homeTeamId={homeTeamId} awayTeamId={awayTeamId} />}
                        {event.typeDescKey === 'blocked-shot' && <BlockEvent event={event} getPlayerName={getPlayerName} getTeamAbbreviation={getTeamAbbreviation} />}
                        {event.typeDescKey === 'takeaway' && <TakeawayEvent event={event} getPlayerName={getPlayerName} getTeamAbbreviation={getTeamAbbreviation} />}
                        {event.typeDescKey === 'giveaway' && <GiveawayEvent event={event} getPlayerName={getPlayerName} getTeamAbbreviation={getTeamAbbreviation} />}
                        {event.typeDescKey === 'missed-shot' && <MissedShotEvent event={event} getPlayerName={getPlayerName} getTeamAbbreviation={getTeamAbbreviation} />}
                        {event.typeDescKey === 'period-start' && <PeriodStartEvent event={event} />}
                    </li>
                ))}
            </ul>
            <div className="flex justify-center mt-4">
                <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-blue-300"
                >
                    Previous
                </button>
                <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={indexOfLastEvent >= filteredEvents.length}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-blue-300 ml-2"
                >
                    Next
                </button>
            </div>
            {selectedEvent && (
                <div className="mt-6 bg-gray-100 p-4 rounded-lg">
                    <h3 className="text-xl font-bold mb-2">Debug: Selected Event JSON</h3>
                    <pre className="text-sm text-gray-800">{JSON.stringify(selectedEvent, null, 2)}</pre>
                </div>
            )}
        </div>
    );
};

export default GameEventList;
